// Library grounding system for AI assistant
import availableLibraries from '../../config/available-libraries.json'

/**
 * Get formatted library information for AI context
 * @returns {string} Formatted library grounding text
 */
export function getLibraryGrounding() {
  let grounding = '\n\n=== AVAILABLE LIBRARIES (SYSTEM-WIDE) ===\n\n'
  
  grounding += 'CRITICAL: Only suggest libraries from this list!\n\n'
  
  // Add preferred libraries by category
  Object.entries(availableLibraries.categories).forEach(([category, libs]) => {
    if (libs.preferred && libs.preferred.length > 0) {
      grounding += `\n**${category.toUpperCase()} - PREFERRED:**\n`
      libs.preferred.forEach(lib => {
        if (lib.cdn && lib.cdn !== 'NOT_ON_CDNJS') {
          grounding += `- ${lib.name} v${lib.version}: ${lib.description}\n`
          grounding += `  CDN: ${lib.cdn}\n`
          grounding += `  WHY: ${lib.reason}\n`
          
          // Add module type and critical notes
          if (lib.moduleType === 'esm') {
            grounding += `  ⚠️ REQUIRES: <script type='module'> tag\n`
          }
          if (lib.note) {
            grounding += `  NOTE: ${lib.note}\n`
          }
          
          // Add usage example if provided
          if (lib.usage) {
            grounding += `  USAGE: ${lib.usage}\n`
          }
          
          // Add common addons for three.js
          if (lib.commonAddons && Object.keys(lib.commonAddons).length > 0) {
            grounding += `  COMMON ADDONS (use these exact URLs):\n`
            Object.entries(lib.commonAddons).forEach(([name, url]) => {
              grounding += `    - ${name}: ${url}\n`
            })
          }
          
          // Add example code if provided
          if (lib.exampleCode) {
            grounding += `  EXAMPLE:\n${lib.exampleCode.split('\\n').map(line => '    ' + line).join('\n')}\n`
          }
        }
      })
    }
    
    if (libs.allowed && libs.allowed.length > 0) {
      grounding += `\n**${category.toUpperCase()} - ALLOWED (suggest if preferred doesn't fit):**\n`
      libs.allowed.forEach(lib => {
        grounding += `- ${lib.name}${lib.version ? ' v' + lib.version : ''}: ${lib.description}\n`
        if (lib.cdn && lib.cdn !== 'NOT_ON_CDNJS') {
          grounding += `  CDN: ${lib.cdn}\n`
        } else if (lib.cdn === 'NOT_ON_CDNJS') {
          grounding += `  ⚠️ NOT ON CDNJS: ${lib.note}\n`
        }
        if (lib.reason) {
          grounding += `  WHY: ${lib.reason}\n`
        }
      })
    }
  })
  
  // Add discouraged libraries
  if (availableLibraries.discouraged && availableLibraries.discouraged.length > 0) {
    grounding += '\n**DISCOURAGED LIBRARIES (DO NOT SUGGEST):**\n'
    availableLibraries.discouraged.forEach(lib => {
      grounding += `- ❌ ${lib.name}: ${lib.reason}\n`
      if (lib.alternative) {
        grounding += `  → Alternative: ${lib.alternative}\n`
      }
    })
  }
  
  // Add AI guidelines
  grounding += '\n**AI SUGGESTION RULES:**\n'
  availableLibraries.aiGuidelines.suggestionRules.forEach(rule => {
    grounding += `- ${rule}\n`
  })
  
  return grounding
}

/**
 * Get library by name for detailed information
 * @param {string} libraryName - Name of library to find
 * @returns {object|null} Library object or null
 */
export function getLibraryInfo(libraryName) {
  for (const [, libs] of Object.entries(availableLibraries.categories)) {
    // Check preferred
    if (libs.preferred) {
      const found = libs.preferred.find(lib => 
        lib.name.toLowerCase() === libraryName.toLowerCase()
      )
      if (found) return { ...found, preference: 'preferred' }
    }
    
    // Check allowed
    if (libs.allowed) {
      const found = libs.allowed.find(lib => 
        lib.name.toLowerCase() === libraryName.toLowerCase()
      )
      if (found) return { ...found, preference: 'allowed' }
    }
  }
  
  // Check discouraged
  const discouraged = availableLibraries.discouraged.find(lib => 
    lib.name.toLowerCase() === libraryName.toLowerCase()
  )
  if (discouraged) return { ...discouraged, preference: 'discouraged' }
  
  return null
}

/**
 * Get all libraries in a category
 * @param {string} category - Category name
 * @returns {object} Preferred and allowed libraries
 */
export function getLibrariesByCategory(category) {
  return availableLibraries.categories[category] || { preferred: [], allowed: [] }
}

/**
 * Get list of all library names for autocomplete/validation
 * @returns {string[]} Array of library names
 */
export function getAllLibraryNames() {
  const names = []
  
  Object.values(availableLibraries.categories).forEach(libs => {
    if (libs.preferred) {
      libs.preferred.forEach(lib => names.push(lib.name))
    }
    if (libs.allowed) {
      libs.allowed.forEach(lib => names.push(lib.name))
    }
  })
  
  return names
}

/**
 * Check if a library is available and get recommendation
 * @param {string} libraryName - Library to check
 * @returns {object} Status and recommendation
 */
export function checkLibrary(libraryName) {
  const info = getLibraryInfo(libraryName)
  
  if (!info) {
    return {
      available: false,
      message: `Library "${libraryName}" is not in the approved list. Search available libraries or suggest an alternative.`
    }
  }
  
  if (info.preference === 'discouraged') {
    return {
      available: false,
      discouraged: true,
      reason: info.reason,
      alternative: info.alternative,
      message: `"${libraryName}" is discouraged. ${info.reason}. Alternative: ${info.alternative}`
    }
  }
  
  if (info.cdn === 'NOT_ON_CDNJS') {
    return {
      available: true,
      preference: info.preference,
      requiresDownload: true,
      note: info.note,
      message: `"${libraryName}" is ${info.preference} but requires manual download: ${info.note}`
    }
  }
  
  return {
    available: true,
    preference: info.preference,
    cdn: info.cdn,
    description: info.description,
    reason: info.reason,
    message: `"${libraryName}" is ${info.preference}. ${info.reason}`
  }
}

export default {
  getLibraryGrounding,
  getLibraryInfo,
  getLibrariesByCategory,
  getAllLibraryNames,
  checkLibrary,
  data: availableLibraries
}
